import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThLarge, faList } from '@fortawesome/free-solid-svg-icons';
import { getErrorMessage, logError } from '../../../utils/errorHandler';
import Toast from '../../Shared/Toast';
import Tooltip from '../ItemTooltip';
import OffseasonNotice from '../../Shared/OffseasonNotice';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import styles from './Beasts.module.css';

interface Mercenary {
  id: number;
  name: string;
  level: number;
  dailyBeast: number;
}

interface LootItem {
  itemId?: number;
  itemName: string;
  itemType: string;
  dropType: string;
  minQuantity: number;
  maxQuantity: number;
}

interface Beast {
  id: number;
  name: string;
  description: string;
  level: number;
  minRequiredLevel: number;
  maxRequiredLevel: number;
  imageUrl: string;
  
  // Stat brackets instead of exact values
  healthBracket: string;
  strengthBracket: string;
  enduranceBracket: string;
  initiativeBracket: string;
  dodgeBracket: string;
  
  isBoss: boolean;
  isActive: boolean;

  // Server-wide records
  highestHitRecorded: number;
  highestCritRecorded: number;
  
  // Sanitized loot (no drop percentages, only items with 5%+ drop rate)
  loot: LootItem[];
}

const Beasts: React.FC = () => {
  const [beasts, setBeasts] = useState<Beast[]>([]);
  const [mercenary, setMercenary] = useState<Mercenary | null>(null);
  const [filterByLevel, setFilterByLevel] = useState<boolean>(() => {
    const savedFilter = localStorage.getItem('filterByLevel');
    return savedFilter ? JSON.parse(savedFilter) : false;
  });
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [resetCountdown, setResetCountdown] = useState<string>('');
  const { dragonInvasionActive: invasionActive, dragonInvasionEndsAt: invasionEndsAt } = useSocket();

  // Countdown to midnight UTC (cron reset time)
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date(Date.UTC(
        now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0
      ));
      const diff = midnight.getTime() - now.getTime();
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setResetCountdown(`${h}h ${m.toString().padStart(2,'0')}m ${s.toString().padStart(2,'0')}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const autoFightHandled = useRef(false);
  const { mercenaryId } = useMercenary();

  useEffect(() => {
    const fetchMercenary = async () => {
      if (mercenaryId) {
        try {
          const response = await axios.get(`/api/mercenary/${mercenaryId}`, { withCredentials: true });
          setMercenary(response.data);
        } catch (error) {
          logError(error, 'Beasts.fetchMercenary');
          displayToast(getErrorMessage(error, 'Failed to load mercenary details'), 'error');
        }
      }
    };

    fetchMercenary();
  }, [mercenaryId]);

  useEffect(() => {
    const fetchBeasts = async () => {
      try {
        const response = await axios.get('/api/beast/', { withCredentials: true });
        setBeasts(response.data);
      } catch (error) {
        logError(error, 'Beasts.fetchBeasts');
        displayToast(getErrorMessage(error, 'Failed to load beasts'), 'error');
      }
    };

    fetchBeasts();
  }, []);

  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const response = await axios.get('/api/user/settings', { withCredentials: true });
        const mode = response.data?.beastViewMode;
        if (mode === 'cards' || mode === 'table') {
          setViewMode(mode);
        }
      } catch (error) {
        logError(error, 'Beasts.fetchUserSettings');
      }
    };

    fetchUserSettings();
  }, []);

  useEffect(() => {
    localStorage.setItem('filterByLevel', JSON.stringify(filterByLevel));
  }, [filterByLevel]);

  const handlePrepareBattle = (beast: Beast) => {
    if (mercenary) {
      navigate('/beast-battle', { state: { beast, mercenaryId: mercenary.id } });
    } else {
      displayToast('No mercenary selected for the battle.', 'error');
    }
  };

  // Auto-trigger prepare screen when arriving from a Fight Again redirect
  useEffect(() => {
    const autoFightName = (location.state as any)?.autoFight as string | undefined;
    if (!autoFightName || autoFightHandled.current || beasts.length === 0 || !mercenary) return;
    autoFightHandled.current = true;
    const target = beasts.find((b) => b.name === autoFightName);
    if (target) {
      handlePrepareBattle(target);
    }
  }, [beasts, mercenary, location.state]);

  const persistUserSettings = async (mode: 'cards' | 'table') => {
    try {
      await axios.put(
        '/api/user/settings',
        { beastViewMode: mode },
        { withCredentials: true }
      );
    } catch (error) {
      logError(error, 'Beasts.persistUserSettings');
    }
  };

  const handleViewModeChange = (mode: 'cards' | 'table') => {
    setViewMode(mode);
    persistUserSettings(mode);
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const filteredBeasts = filterByLevel
    ? beasts.filter(
        (beast) =>
          mercenary !== null &&
          mercenary.level >= beast.minRequiredLevel &&
          mercenary.level <= beast.maxRequiredLevel &&
          !beast.isBoss
      )
    : beasts;

  return (
    <OffseasonNotice feature="beast battles">
      <div className={styles.beastsPage}>
      {invasionActive && (
        <div style={{
          background: 'linear-gradient(135deg, #120d03, #1c1508)',
          border: '1px solid rgba(190, 130, 35, 0.5)',
          borderRadius: 8,
          margin: '0 0 16px',
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          boxShadow: '0 4px 20px rgba(180, 120, 30, 0.2)',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#d4a843', fontFamily: 'Cinzel, serif', letterSpacing: 0.5 }}>
              Dragon Invasion is Active!
            </div>
            <div style={{ fontSize: '0.82rem', color: '#a8854a', marginTop: 2 }}>
              The invasion dragons have emerged. Hunt them for exclusive Dragon materials.
              {invasionEndsAt && (() => {
                const diff = new Date(invasionEndsAt).getTime() - Date.now();
                if (diff > 0) {
                  const h = Math.floor(diff / 3600000);
                  const m = Math.floor((diff % 3600000) / 60000);
                  return ` Ends in ${h}h ${m}m.`;
                }
                return '';
              })()}
            </div>
          </div>
        </div>
      )}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      <section className={styles.heroPanel}>
        <div className={styles.heroLeft}>
          <div>
            <h1 className={styles.title}>Beast Battles</h1>
            <p className={styles.subtitle}>
              Train your mercenary, and claim your spoils!
            </p>
          </div>
          <div className={styles.viewToggleRow}>
            <div className={styles.toggleGroup}>
              <button
                type="button"
                className={`${styles.toggleButton} ${viewMode === 'cards' ? styles.activeToggle : ''}`}
                onClick={() => handleViewModeChange('cards')}
                title="Card View"
              >
                <FontAwesomeIcon icon={faThLarge} /> <span>Cards</span>
              </button>
              <button
                type="button"
                className={`${styles.toggleButton} ${viewMode === 'table' ? styles.activeToggle : ''}`}
                onClick={() => handleViewModeChange('table')}
                title="Table View"
              >
                <FontAwesomeIcon icon={faList} /> <span>Table</span>
              </button>
            </div>
          </div>
        </div>
        <div className={styles.heroRight}>
          {mercenary && (
            <div className={styles.dailyCard}>
              <span className={styles.dailyLabel}>Daily Beast Battles</span>
              <span className={styles.dailyValue}>{mercenary.dailyBeast} / 50</span>
              <p className={styles.dailyNote}>
                Resets in <span className={styles.dailyCountdown}>{resetCountdown}</span>
              </p>
            </div>
          )}
          <label className={`${styles.filterToggle} ${styles.heroFilter}`}>
            <input
              type="checkbox"
              checked={filterByLevel}
              onChange={(e) => setFilterByLevel(e.target.checked)}
            />
            Show beasts for my level
          </label>
        </div>
      </section>

      {viewMode === 'cards' ? (
        <div className={styles.beastGrid}>
          {filteredBeasts.length > 0 ? (
            filteredBeasts.map((beast) => (
              <article key={beast.id} className={styles.beastCard}>
                <div className={styles.cardHeader}>
                  <div>
                    <h3 className={styles.beastName}>{beast.name}</h3>
                  </div>
                  <div className={styles.chips}>
                    <span className={styles.levelChip}>Lvl {beast.minRequiredLevel} – {beast.maxRequiredLevel}</span>
                    {beast.isBoss && <span className={styles.bossChip}>Boss</span>}
                  </div>
                </div>

                <div className={styles.cardBody}>
                  <p className={styles.beastDescription}>{beast.description}</p>

                  <div className={styles.statGrid}>
                    <div className={styles.statPill}><span className={styles.statLabel}>HP</span><span className={styles.statValue}>{beast.healthBracket}</span></div>
                    <div className={styles.statPill}><span className={styles.statLabel}>STR</span><span className={styles.statValue}>{beast.strengthBracket}</span></div>
                    <div className={styles.statPill}><span className={styles.statLabel}>END</span><span className={styles.statValue}>{beast.enduranceBracket}</span></div>
                    <div className={styles.statPill}><span className={styles.statLabel}>INIT</span><span className={styles.statValue}>{beast.initiativeBracket}</span></div>
                    <div className={styles.statPill}><span className={styles.statLabel}>DODGE</span><span className={styles.statValue}>{beast.dodgeBracket}</span></div>
                  </div>

                  <div className={styles.lootBlock}>
                    {beast.loot && beast.loot.filter((i) => i.dropType !== 'silver').length > 0 ? (
                      <div className={styles.lootList}>
                        {beast.loot
                          .filter((item) => item.dropType !== 'silver')
                          .map((item, idx) => (
                            <div key={idx} className={styles.lootItem}>
                              {item.itemId ? (
                                <Tooltip itemId={item.itemId}>
                                  <span className={styles.lootName}>{item.itemName}</span>
                                </Tooltip>
                              ) : (
                                <span className={styles.lootName}>{item.itemName}</span>
                              )}
                              {item.minQuantity !== item.maxQuantity && (
                                <span className={styles.lootQty}>
                                  {item.minQuantity}-{item.maxQuantity}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className={styles.unknownLoot}>Unknown spoils</p>
                    )}
                  </div>
                </div>

                <div className={styles.cardFooter}>
                  <button onClick={() => handlePrepareBattle(beast)} className={styles.ctaButton}>
                    Prepare Battle
                  </button>
                </div>
              </article>
            ))
          ) : (
            <p className={styles.noBeastsMessage}>No beasts available for the selected filter.</p>
          )}
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {filteredBeasts.length > 0 ? (
            <table className={styles.beastTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Level Range</th>
                  <th className={styles.statsCol}>Stats</th>
                  <th>Loot</th>
                  <th className={styles.actionCol}></th>
                </tr>
              </thead>
              <tbody>
                {filteredBeasts.map((beast) => (
                  <tr key={beast.id}>
                    <td>
                      <div className={styles.nameCell} onClick={() => handlePrepareBattle(beast)} role="button" tabIndex={0}>
                        <div className={styles.nameLine}>{beast.name}</div>
                        <div className={styles.descLine}>{beast.description}</div>
                      </div>
                    </td>
                    <td className={styles.levelCell}><span className={styles.levelPrefix}>Lvl </span>{beast.minRequiredLevel} – {beast.maxRequiredLevel}</td>
                    <td className={styles.statsCol}>
                      <div className={styles.statInline}>
                        <span>HP {beast.healthBracket}</span>
                        <span>STR {beast.strengthBracket}</span>
                        <span>END {beast.enduranceBracket}</span>
                        <span>INIT {beast.initiativeBracket}</span>
                        <span>DODGE {beast.dodgeBracket}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.lootInline}>
                        {beast.loot && beast.loot.filter((i) => i.dropType !== 'silver').length > 0 ? (
                          beast.loot
                            .filter((item) => item.dropType !== 'silver')
                            .map((item, idx) => (
                              <span key={idx} className={styles.lootInlineItem}>
                                {item.itemId ? (
                                  <Tooltip itemId={item.itemId}>
                                    <span className={styles.lootName}>{item.itemName}</span>
                                  </Tooltip>
                                ) : (
                                  <span className={styles.lootName}>{item.itemName}</span>
                                )}
                                {item.minQuantity !== item.maxQuantity && ` (${item.minQuantity}-${item.maxQuantity})`}
                              </span>
                            ))
                        ) : (
                          <span className={styles.unknownLoot}>Unknown</span>
                        )}
                      </div>
                    </td>
                    <td className={`${styles.actionCell} ${styles.actionCol}`}>
                      <button onClick={() => handlePrepareBattle(beast)} className={styles.ctaButton} title="Prepare Battle">
                        <span className={styles.buttonText}>Prepare</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.noBeastsMessage}>No beasts available for the selected filter.</p>
          )}
        </div>
      )}
      </div>
    </OffseasonNotice>
  );
};

export default Beasts;
